import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { useQuery } from "@tanstack/react-query";
import { FC, Fragment, useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Text } from "@radix-ui/themes";
import { atlasRoutes } from "@/app/router/routes";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import { SpaceAccountResetDialog } from "@/pages/space-accounts/view/space-account-reset-dialog";

export const SpaceAccountsViewPage: FC = () => {
  const navigate = useNavigate();

  const { spaceAccountId } = useParams<{ spaceAccountId: Uuid }>();
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);

  if (!spaceAccountId) {
    return <div>Invalid ID</div>;
  }

  const result = useQuery({
    queryKey: ["space-account-by-id", spaceAccountId],
    queryFn: () =>
      api.get("/internal/v1/space-accounts/{space_account_id}", {
        params: {
          path: {
            space_account_id: spaceAccountId,
          },
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const usageStats = useQuery({
    queryKey: ["space-account-usage-stats", spaceAccountId],
    queryFn: () =>
      api.get("/internal/v1/space-accounts/{space_account_id}/usage-stats", {
        params: { path: { space_account_id: spaceAccountId } },
      }),
  });

  const spaceAccount = result.data?.data;

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return "—";
    }

    const parsed = DateTime.fromISO(value);
    if (!parsed.isValid) {
      return value;
    }

    return parsed.toFormat("MMM dd, yyyy h:mm a");
  };

  const keyDates: { label: string; value?: string | null }[] = [
    { label: "Created At", value: spaceAccount?.created_at },
    { label: "Provisioned At", value: spaceAccount?.provisioned_at },
    { label: "Deprovisioned At", value: spaceAccount?.deprovisioned_at },
    { label: "Deleted At", value: spaceAccount?.deleted_at ?? spaceAccount?.soft_deleted_at },
  ];

  const handleGenerateToken = async () => {
    const confirmation = prompt(
      "Type 'CONFIRM' to proceed.\n\nThis action will be recorded internally for auditing purposes."
    );

    if (confirmation === "CONFIRM") {
      const response = await api.post(
        "/internal/v1/space-accounts/{space_account_id}/generate-oauth-access-token",
        {
          params: {
            path: {
              space_account_id: spaceAccountId,
            },
          },
        }
      );

      setGeneratedToken(response.data?.token ?? null);
    } else {
      alert("Cancelled.");
    }
  };
  const fetchAndGoToAccountTransitionInfo = useCallback(async () => {
    const accountTransitionInfoResult = await api.get(
      "/internal/v1/space-accounts/{space_account_id}/account-transition-info",
      {
        params: {
          path: {
            space_account_id: spaceAccountId,
          },
        },
      }
    );

    const accountTransitionInfoId = accountTransitionInfoResult.data?.id;

    if (!accountTransitionInfoId) {
      alert("No account transition info found.");
      return;
    }

    navigate(atlasRoutes.accountTransitionInfoView({ params: { accountTransitionInfoId } }).path);
  }, [spaceAccountId, navigate]);

  const mixpanelLink = `https://mixpanel.com/project/2940147/view/3463887/app/profile#distinct_id=${result.data?.data?.id}`;

  const firebaseCrashlyticsLink = `https://console.firebase.google.com/u/0/project/memvp-25499/crashlytics/app/ios:com.mem-labs.mem/search?time=last-seven-days&q=${result.data?.data?.account_id?.toUpperCase()}`;

  const stripeCustomerId = result.data?.data?.stripe_customer_id ?? undefined;
  const stripeCustomerLink = stripeCustomerId
    ? `https://dashboard.stripe.com/customers/${stripeCustomerId}`
    : undefined;

  const externalLinks = [
    { href: mixpanelLink, label: "View Mixpanel" },
    { href: firebaseCrashlyticsLink, label: "View Firebase Crashlytics" },
    ...(stripeCustomerLink ? [{ href: stripeCustomerLink, label: "View Stripe Customer" }] : []),
  ];

  return (
    <div>
      <div>Profile Email: {result.data?.data?.profile_email_address ?? "-"}</div>
      <KeyDatesSection>
        <SectionTitle size="3">Key Dates</SectionTitle>
        <DefinitionList>
          {keyDates.map(({ label, value }) => (
            <Fragment key={label}>
              <DefinitionLabel>{label}</DefinitionLabel>
              <DefinitionValue>{formatDateTime(value)}</DefinitionValue>
            </Fragment>
          ))}
        </DefinitionList>
      </KeyDatesSection>
      <Button
        variant="outline"
        onClick={() => {
          if (!result.data?.data?.account_id) {
            alert("No account ID found.");
            return;
          }

          navigate(
            atlasRoutes.accountsView({ params: { accountId: result.data?.data?.account_id } }).path
          );
        }}
      >
        View Account
      </Button>
      <br />
      <Button
        variant="outline"
        onClick={() =>
          navigate(atlasRoutes.spaceAccountSyncClientGroups({ params: { spaceAccountId } }).path)
        }
      >
        View Sync Client Groups
      </Button>
      <br />
      <Button
        variant="outline"
        onClick={() =>
          navigate(atlasRoutes.spaceAccountDataImports({ params: { spaceAccountId } }).path)
        }
      >
        View Data Imports
      </Button>
      <br />
      <Button
        variant="outline"
        onClick={() => navigate(`${atlasRoutes.imports({}).path}?owner=${spaceAccountId}`)}
      >
        View Imports Started By This Space Account
      </Button>
      <br />
      <Button variant="outline" onClick={fetchAndGoToAccountTransitionInfo}>
        View Account Transition Info
      </Button>
      <br />
      <Button
        variant="outline"
        onClick={() =>
          navigate(
            atlasRoutes.spaceAccountSpaceAccountAppCallouts({ params: { spaceAccountId } }).path
          )
        }
      >
        View Space Account App Callouts
      </Button>
      <br />
      <SpaceAccountResetDialog
        spaceAccountId={spaceAccountId}
        onAfterReset={async () => {
          await Promise.all([result.refetch(), usageStats.refetch()]);
        }}
      />
      <br />
      <br />
      <div>
        {externalLinks.map((item, idx) => (
          <span key={`${item.label}-${idx}`}>
            <a href={item.href} target="_blank" rel="noopener noreferrer">
              {item.label}
            </a>
            {idx < externalLinks.length - 1 ? " | " : null}
          </span>
        ))}
      </div>
      <br />
      <br />
      <Button variant="outline" onClick={handleGenerateToken}>
        Generate Oauth Access Token
      </Button>
      {generatedToken && (
        <div style={{ marginTop: "20px" }}>
          <div>Your generated token:</div>
          <pre>{generatedToken}</pre>
          <a href="https://v2.mem.ai/login/oauth-token" target="_blank" rel="noopener noreferrer">
            Go to OAuth Access Token Page
          </a>
        </div>
      )}
      <br />
      <h1>View Space Account</h1>
      <pre>{JSON.stringify(result.data, null, 2)}</pre>
      <br />
      <h2>Usage Stats</h2>
      <pre>{JSON.stringify(usageStats.data, null, 2)}</pre>
    </div>
  );
};

const KeyDatesSection = styled.section`
  margin-top: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled(Text)`
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
`;

const DefinitionList = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 16px;
  row-gap: 8px;
  margin: 0;
`;

const DefinitionLabel = styled.dt`
  font-weight: 500;
  color: var(--gray-12);
`;

const DefinitionValue = styled.dd`
  margin: 0;
`;
